/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2025, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BULB_BUSY_COVER_SERVICE } from '../../angularjs.providers';
import { TranslationService } from '../../../common/translation.service';

interface Tag {
  id?: string;
  name: string;
  description?: string;
  colorCode: string;
  // expectedUses: number;
  grades: any[],
  active: boolean;
}

/**
 * Component that loads when Org admin clicks Tag button.
 */
@Component({
  selector: 'bulb-tags-dialog',
  templateUrl: './tags-dialog.component.html',
  styleUrls: ['./tags-dialog.component.scss'],
  standalone: false,
})
export class TagsDialogComponent implements OnInit {

  existingTag: Tag;

  tag: Tag;

  isOrgTag: boolean;

  organizationId: string;

  gradeOptions = [
    { code: 'PRESCHOOL', name: this.$translate.translateInstant('organization_dashboard_preschool_letter') },
    {
      code: 'KINDERGARTEN',
      name: this.$translate.translateInstant('organization_dashboard_kindergarten_letter'),
    },
    { code: 'FIRST', name: '1' }, { code: 'SECOND', name: '2' },
    { code: 'THIRD', name: '3' }, { code: 'FOURTH', name: '4' },
    { code: 'FIFTH', name: '5' }, { code: 'SIXTH', name: '6' },
    { code: 'SEVENTH', name: '7' }, { code: 'EIGHTH', name: '8' },
    { code: 'NINTH', name: '9' }, { code: 'TENTH', name: '10' },
    { code: 'ELEVENTH', name: '11' }, { code: 'TWELFTH', name: '12' },
    { code: 'POSTGRADUATE', name: this.$translate.translateInstant('grade_level_POSTGRADUATE') },
    { code: 'OTHER', name: this.$translate.translateInstant('organization_dashboard_other_letters') },
    { code: 'UNASSIGNED', name: this.$translate.translateInstant('grade_level_UNASSIGNED') },
  ];

  constructor(
    private httpClient: HttpClient,
    private $translate: TranslationService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService: any,
    @Inject('BulbErrorService') private bulbErrorService,
  ) {}

  ngOnInit(): void {
    this.organizationId = this.config.data.organizationId;
    this.existingTag = this.config.data.tag;
    this.isOrgTag = this.config.data.isOrgTag;
    this.tag = this.resetNewTag(this.existingTag);
  }

  resetNewTag(tag?: any): Tag {
    return {
      id: tag?.id ?? null,
      name: tag?.name ?? '',
      description: tag?.description ?? '',
      colorCode: tag?.colorCode ?? '#00AEEF',
      // expectedUses: tag?.expectedUses ?? null,
      grades: tag?.grades ? [...tag.grades] : [],
      active: tag?.active ?? true,
    };
  }

  addTag(form) {
    const tagDto = angular.copy(this.tag);

    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();

    const url = this.isOrgTag ? `tags/organization/${this.organizationId}` : 'tags';

    const request$ = this.existingTag
      ? this.httpClient.patch(`${url}/${tagDto.id}`, tagDto)
      : this.httpClient.post(url, tagDto);

    request$.pipe(
      finalize(() => {
        stopBusyIndicatorCallback();
      }),
    ).subscribe(
      {
        next: (tag: any) => {
          this.tag.id = tag.id;
          this.ref.close(this.tag);
          this.tag = this.resetNewTag();
          form.resetForm();
        },
        error: (err) => {
          this.bulbErrorService.showError(null, err);
        },
      },
    );
  }

  closePopUp() {
    this.ref.close(null);
  }
}
